import type { DateValidationError } from '@mui/x-date-pickers';
import type { FieldError } from 'react-hook-form';

import { TodayOutlined } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { theme } from '@theguarantors/tg-package-material-ui';
import { DateFormat } from '@/shared/libs/time.lib';

type LeaseDatePickerProps = {
  title: string;
  value: dayjs.Dayjs | null;
  onChange: (newValue: dayjs.Dayjs | null) => void;
  onError: (error: DateValidationError, value: dayjs.Dayjs | null) => void;
  error?: FieldError;
  required?: boolean;
  dataTestId: string;
  minDate?: dayjs.Dayjs;
  maxDate?: dayjs.Dayjs;
};

export const LeaseDatePicker = ({
  title,
  value,
  onChange,
  onError,
  error,
  required = false,
  dataTestId,
  minDate,
  maxDate,
}: LeaseDatePickerProps) => {
  return (
    <DatePicker
      label={title}
      value={value}
      openTo="day"
      views={['year', 'day']}
      format={DateFormat.Us}
      minDate={minDate ?? dayjs().subtract(14, 'day')}
      maxDate={maxDate}
      onChange={onChange}
      onError={onError}
      slots={{
        openPickerIcon: TodayOutlined,
      }}
      slotProps={{
        actionBar: {
          sx: styles.actionBar,
        },
        textField: {
          id: dataTestId,
          variant: 'filled',
          size: 'small',
          required,
          helperText: error?.message?.toString() ?? null,
          error: Boolean(error),
          'aria-label': `${title} - text field`,
        },
        openPickerButton: {
          'aria-label': `${title} - open calendar`,
        },
        // Calendar cell styling based on Figma design
        // Unselected: hover=#E7E7E7, focused/pressed=#D1D1D1
        // Selected: bg=#080A2D, text=#FFFFFF
        // Font: 16px, border-radius: 100px (pill shape)
        day: { sx: styles.day },
        yearButton: { sx: styles.yearButton },
        monthButton: { sx: styles.monthButton },
      }}
      sx={styles.datePicker}
    />
  );
};

const styles = {
  actionBar: {
    padding: '8px 12px 12px 12px',
    justifyContent: 'flex-end',
  },
  datePicker: {
    width: '100%',
    // Filled variant styled like outlined - transparent bg, rounded, box-shadow border
    '& .MuiFilledInput-root, & .MuiPickersFilledInput-root': {
      backgroundColor: 'transparent !important',
      borderRadius: '4px !important',
      // Remove the underline completely
      '&::before': {
        display: 'none !important',
      },
      '&::after': {
        display: 'none !important',
      },
      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent !important',
      },
    },
    // Gray border via box-shadow - only when NOT in error state
    // No space before :not() - targets the element directly
    '& .MuiFilledInput-root:not(.Mui-error), & .MuiPickersFilledInput-root:not(.Mui-error)': {
      border: 'none',
      boxShadow: '#888888 0px 0px 0px 1px inset !important',
      '&:hover': {
        boxShadow: '#1D1D1D 0px 0px 0px 1px inset !important',
      },
      '&.Mui-focused': {
        boxShadow: '#1D1D1D 0px 0px 0px 2px inset !important',
      },
    },
    '& .MuiButtonBase-root': {
      marginRight: '-4px !important',
    },
    // Label color - rgb(93, 93, 93)
    '& .MuiInputLabel-root, & .MuiFormLabel-root': {
      color: '#5D5D5D',
      '&.Mui-focused': {
        color: '#5D5D5D',
      },
    },
    // Red asterisk for required fields
    '& .MuiFormLabel-asterisk': {
      color: `${theme.palette.red[600]} !important`,
    },
  },
  day: {
    fontSize: '16px',
    // Unselected hover/focus
    '&:not(.Mui-selected)': {
      '&:hover': {
        backgroundColor: '#E7E7E7',
      },
      '&:focus, &:active': {
        backgroundColor: '#D1D1D1',
      },
    },
    // Selected state
    '&.Mui-selected': {
      backgroundColor: '#080A2D !important',
      color: '#FFFFFF !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: '#080A2D !important',
        color: '#FFFFFF !important',
      },
    },
  },
  monthButton: {
    fontSize: '16px',
    borderRadius: '100px',
    // Unselected hover/focus
    '&:not(.Mui-selected)': {
      '&:hover': {
        backgroundColor: '#E7E7E7',
      },
      '&:focus, &:active': {
        backgroundColor: '#D1D1D1',
      },
    },
    // Selected state
    '&.Mui-selected': {
      backgroundColor: '#080A2D !important',
      color: '#FFFFFF !important',
      '&:hover, &:focus, &:active': {
        backgroundColor: '#080A2D !important',
        color: '#FFFFFF !important',
      },
    },
  },
  yearButton: {
    fontSize: '16px',
    fontWeight: 400,
    borderRadius: '100px',
    // Unselected hover/focus
    '&:not(.Mui-selected)': {
      '&:hover': {
        backgroundColor: '#E7E7E7',
      },
      '&:focus, &:active': {
        backgroundColor: '#D1D1D1',
      },
    },
    // Selected state
    '&.Mui-selected': {
      backgroundColor: '#080A2D !important',
      color: '#FFFFFF !important',
      fontWeight: 400,
      '&:hover, &:focus, &:active': {
        backgroundColor: '#080A2D !important',
        color: '#FFFFFF !important',
      },
    },
  },
};
